import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, Eye } from "lucide-react";
import { getRecentlyViewed } from "../../lib/recentlyViewed";
import { Sparkle } from "../doodles/Doodles";

export default function RecentlyViewed({ excludeId, title = "Recently viewed" }) {
  const [items, setItems] = useState([]);
  const scrollerRef = useRef(null);

  useEffect(() => {
    const refresh = () => {
      const all = getRecentlyViewed();
      setItems(excludeId ? all.filter((p) => p.id !== excludeId) : all);
    };
    refresh();
    window.addEventListener("nc:recently-viewed-updated", refresh);
    window.addEventListener("storage", refresh);
    return () => {
      window.removeEventListener("nc:recently-viewed-updated", refresh);
      window.removeEventListener("storage", refresh);
    };
  }, [excludeId]);

  if (items.length === 0) return null;

  const scrollBy = (dir) => {
    const el = scrollerRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * (el.clientWidth * 0.7), behavior: "smooth" });
  };

  return (
    <section className="relative section-y overflow-hidden" data-testid="recently-viewed-section">
      <Sparkle color="#FFD93D" className="absolute top-4 right-12 w-6 animate-twinkle pointer-events-none"/>
      <Sparkle color="#FF6B9D" className="absolute bottom-6 left-8 w-5 animate-twinkle pointer-events-none" style={{ animationDelay: "0.6s" }}/>

      <div className="container-x">
        <div className="flex items-end justify-between gap-3 mb-5">
          <div>
            <p className="label-small text-xs sm:text-sm flex items-center gap-2"><Eye className="w-4 h-4"/> {title}</p>
            <h2 className="font-doodle text-2xl sm:text-3xl text-[#2D2424] mt-1">Pick up where you <span className="text-[#FF6B9D]">left off</span></h2>
          </div>
          <div className="hidden sm:flex items-center gap-2 shrink-0">
            <button
              onClick={() => scrollBy(-1)}
              data-testid="recently-viewed-prev"
              aria-label="scroll left"
              className="w-9 h-9 rounded-full border-2 border-[#2D2424] bg-white hover:bg-[#FFE7F1] flex items-center justify-center"
              style={{ boxShadow: "3px 3px 0 #2D2424" }}
            >
              <ChevronLeft className="w-4 h-4"/>
            </button>
            <button
              onClick={() => scrollBy(1)}
              data-testid="recently-viewed-next"
              aria-label="scroll right"
              className="w-9 h-9 rounded-full border-2 border-[#2D2424] bg-white hover:bg-[#FFE7F1] flex items-center justify-center"
              style={{ boxShadow: "3px 3px 0 #2D2424" }}
            >
              <ChevronRight className="w-4 h-4"/>
            </button>
          </div>
        </div>

        <div
          ref={scrollerRef}
          className="flex gap-3 sm:gap-4 overflow-x-auto scroll-smooth snap-x snap-mandatory pb-2 -mx-4 px-4 sm:mx-0 sm:px-0"
          style={{ scrollbarWidth: "none" }}
          data-testid="recently-viewed-scroller"
        >
          {items.map((p) => (
            <Link
              key={p.id}
              to={`/product/${p.slug}`}
              data-testid={`recent-${p.slug}`}
              className="snap-start shrink-0 w-[140px] sm:w-[160px] doodle-card bg-white overflow-hidden hover:-translate-y-0.5 transition-transform"
            >
              <div className="aspect-[3/4] bg-[#FFE7F1] overflow-hidden">
                {p.image ? (
                  <img src={p.image} alt={p.name} className="w-full h-full object-cover" loading="lazy"/>
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-[#7A1F2B] font-doodle text-3xl">🎁</div>
                )}
              </div>
              <div className="p-2.5">
                <p className="font-hand text-xs text-[#2D2424] line-clamp-2 leading-tight min-h-[2rem]">{p.name}</p>
                <div className="flex items-baseline gap-1.5 mt-1">
                  <span className="font-doodle text-base text-[#7A1F2B]">₹{(p.price || 0).toLocaleString("en-IN")}</span>
                  {p.mrp && p.mrp > p.price && (
                    <span className="text-[10px] line-through text-[#9B9494] font-hand">₹{p.mrp.toLocaleString("en-IN")}</span>
                  )}
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}
